"use client";

import { useEffect, useRef, useState } from "react";
import styles from "./revealTitle.module.scss";

export function RevealTitle({ children, size = "large" }) {
	const containerRef = useRef(null);
	const [progress, setProgress] = useState(0);

	// pick offset based on size
	const offsetRem = size === "large" ? 10 : 2;

	useEffect(() => {
		const el = containerRef.current;
		if (!el) return;

		let rafId;

		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting) {
					const update = () => {
						const rect = el.getBoundingClientRect();
						const vh = window.innerHeight;

						// progress: 0 when element bottom hits viewport bottom
						// progress: 1 when element center hits viewport center
						const start = vh;
						const end = vh * 0.66;
						let p = (start - rect.top) / (start - end);

						p = Math.max(0, Math.min(1, p));
						setProgress(p);

						rafId = requestAnimationFrame(update);
					};
					update();
				}
			},
			{ threshold: [0, 0.25, 0.5, 0.75, 1] }
		);

		observer.observe(el);

		return () => {
			observer.disconnect();
			cancelAnimationFrame(rafId);
		};
	}, []);

	const words = typeof children === "string" ? children.split(" ") : [];
	const totalWords = words.length;

	return (
		<span ref={containerRef} className={styles.wrapper}>
			{words.map((word, i) => {
				// stagger range settings: spread the transition evenly among all words
				const STAGGER = 0.15; // can tune this for overlap, should be < 1/totalWords
				const wordStart = i * STAGGER;
				const wordEnd = wordStart + (1 - STAGGER * (totalWords - 1));

				// progress falls from wordStart to wordEnd for this word
				let clamped = 0;
				if (progress >= wordStart && progress <= wordEnd) {
					clamped = (progress - wordStart) / (wordEnd - wordStart);
				} else if (progress > wordEnd) {
					clamped = 1;
				} else {
					clamped = 0;
				}

				// At clamped=0 → offsetRem, clamped=1 → 0rem
				const translateY = (1 - clamped) * offsetRem;
				const opacity = clamped;

				return (
					<span
						key={i}
						className={styles.word}
						style={{
							transform: `translateY(${translateY}rem)`,
							opacity,
						}}
					>
						{word}
					</span>
				);
			})}
		</span>
	);
}
